import { atom, type Atom, type PrimitiveAtom } from 'jotai';
import upperFirst from 'lodash/upperFirst';
import { type IEnvironment } from 'relay-runtime';

import { withRequestTiming, withTimingAdapter } from '../../common/analytics/withRequestTiming';
import { requestKnowledgeSources, searchCombinedConfig } from '../../utils/appConnectors';
import { requestAvailableProducts } from '../../utils/availableProducts';
import {
	requestCollaborationUsers,
	type CollabGraphRecommendedEntity,
} from '../../utils/collaborationUsers';
import { requestGlobalMetrics, type GlobalMetricsResponse } from '../../utils/globalMetrics';
import {
	requestOrgMetrics,
	type NoDataResponse,
	type OrgMetricsResponse,
} from '../../utils/orgMetrics';
import { STATIC_DATASETS, type StaticDatasetKey } from '../components/DebugPanel/staticDatasets';
import type { AppConnector } from '../components/Sections/ConnectorsSection/AppCard';
import type { AvailableSite } from '../components/SiteSwitcher/types';

/**
 * Selects how `OrgMetricsAtom` sources its payload.
 *
 * - `'api'` (default): fetch from the live `metrics/org/{orgId}` endpoint
 *   using `cloudIdAtom` + `orgIdAtom`.
 * - `'static'`: short-circuit the network call and serve the dataset
 *   identified by `selectedStaticDatasetKeyAtom`.
 *
 * Only intended for development/preview purposes via the debug panel.
 */
export type OrgMetricsSource = 'api' | 'static';

/**
 * Writable atom controlling which source `OrgMetricsAtom` reads from.
 * Defaults to `'api'` so the production logged-in flow is unchanged when
 * the debug panel has not been touched.
 */
export const orgMetricsSourceAtom: PrimitiveAtom<OrgMetricsSource> & {
	init: OrgMetricsSource;
} = atom<OrgMetricsSource>('api');

/**
 * Writable atom holding the currently selected static dataset key for the
 * debug panel. `null` means "no static dataset chosen yet". When
 * `orgMetricsSourceAtom` is `'static'` and this is non-null, `OrgMetricsAtom`
 * resolves synchronously to the matching `OrgMetricsResponse` payload.
 */
export const selectedStaticDatasetKeyAtom: PrimitiveAtom<StaticDatasetKey | null> & {
	init: StaticDatasetKey | null;
} = atom<StaticDatasetKey | null>(null);

/**
 * Writable atom that holds the current cloud ID.
 *
 * Initialised to `null` to represent the "not yet known" state. The real
 * cloud ID is hydrated from the response of the `/gateway/api/app-switcher`
 * `available-products` endpoint (see the `availableSitesAtom` /
 * `LoggedInGate` flow). Async atoms that depend on this value (e.g.
 * `KnowledgeSourcesAtom`, `CollaborationUsersAtom`) early-return an empty
 * idle result while it is `null`, so no network calls are made with an
 * unknown tenant.
 *
 * This atom can be updated at runtime — for example, when the user
 * switches tenants via the `SiteSwitcher` — by calling `set` on it.
 */
export const cloudIdAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

/**
 * Writable atom that holds the current org ID.
 *
 * The initial value is derived from `getConfig().orgId`, which
 * resolves based on the current environment (staging, production, etc.).
 *
 * This atom can be updated at runtime — for example, when the user
 * switches tenants via an app-switcher — by calling `set` on it.
 */
export const orgIdAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

/**
 * Writable atom that holds the current user's Atlassian account ID.
 *
 * Initialised as an empty string and populated by `LoggedInView` once
 * the `UserProvider` has resolved the authenticated user. Modelled as
 * a Jotai atom (rather than read directly from React context) so that
 * async atoms which depend on the account ID — such as
 * `CollaborationUsersAtom` — can be evaluated alongside other data
 * fetching atoms (e.g. `KnowledgeSourcesAtom`) without needing to be
 * wired through React state.
 */
export const accountIdAtom: PrimitiveAtom<string> & {
	init: string;
} = atom<string>('');

/**
 * Holds the active Relay `IEnvironment` so async atoms can issue GraphQL
 * queries without needing to live inside a React component.
 *
 * Hydrated once by `LoggedInView` via `useHydrateAtoms` from the
 * environment provided by `@atlassian/relay-environment-provider`. Atoms
 * that depend on this should treat a `null` value as "not ready yet" and
 * return an empty/idle result instead of firing a network request.
 */
export const relayEnvironmentAtom: PrimitiveAtom<IEnvironment | null> & {
	init: IEnvironment | null;
} = atom<IEnvironment | null>(null);

/**
 * Result type returned by `availableSitesAtom`.
 *
 * On success, `data` is the list of sites the user can access (used by
 * `SiteSwitcher` for rendering and by `LoggedInGate` for resolving the
 * initial `cloudId`). On failure, `data` is empty and `error` carries
 * the message; consumers can fall back gracefully without crashing.
 */
export type AvailableSitesResult = {
	data: AvailableSite[];
	error: string | null;
};

/**
 * Derived async atom that fetches the list of Atlassian sites/products
 * available to the current user via the App Switcher gateway endpoint.
 *
 * This atom is intentionally evaluated *before* `LoggedInView` mounts,
 * so the `LoggedInGate` wrapper can pick the correct `cloudId` from the
 * response and hydrate `cloudIdAtom` with it. Downstream atoms that
 * depend on `cloudIdAtom` (e.g. `KnowledgeSourcesAtom`,
 * `CollaborationUsersAtom`) early-return an idle result while
 * `cloudIdAtom` is `null`, ensuring no network calls are made with an
 * unknown tenant.
 *
 * Never throws — on failure, returns `{ data: [], error: "..." }` so
 * consumers can fall back gracefully.
 *
 * Components using this atom will suspend (React Suspense) until the
 * data is resolved. Wrap the consuming subtree in a
 * `<Suspense fallback={...}>` boundary.
 */
export const availableSitesAtom: Atom<Promise<AvailableSitesResult>> = atom<
	Promise<AvailableSitesResult>
>(async () => {
	try {
		const { execute } = requestAvailableProducts();
		const response = await withRequestTiming(
			'operational.availableProductsRequest.completed.twgMicrositeAvailableProducts',
			execute,
		);
		if (response.success) {
			return { data: response.data.sites, error: null };
		}
		return {
			data: [],
			error: `Failed to fetch available products: ${response.error?.message ?? response.error}`,
		};
	} catch (error) {
		return {
			data: [],
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
});

/**
 * Result type returned by KnowledgeSourcesAtom.
 * On success, `data` contains the sources and `error` is null.
 * On failure, `data` is an empty array and `error` holds the message.
 */
export type AppConnectorResults = {
	data: AppConnector[];
	error: string | null;
};

/**
 * Derived async atom that fetches knowledge sources based on the current
 * cloud ID. The data is now sourced from the GraphQL `searchCombinedConfig`
 * operation against `/gateway/api/graphql` (via Relay's `fetchQuery`),
 * replacing the legacy REST call to `requestKnowledgeSources`.
 *
 * Because it reads from `cloudIdAtom` and `relayEnvironmentAtom`, Jotai
 * will automatically re-fetch whenever either changes (e.g. when the
 * user switches tenants).
 *
 * Never throws — on failure, returns `{ data: [], error: "..." }` so
 * consumers can handle the error gracefully without an ErrorBoundary.
 *
 * Components using this atom will suspend (React Suspense) until
 * the data is resolved. Wrap the consuming component in a
 * `<Suspense fallback={...}>` boundary.
 */
export const KnowledgeSourcesAtom: Atom<Promise<AppConnectorResults>> = atom<
	Promise<AppConnectorResults>
>(async (get) => {
	const cloudId = get(cloudIdAtom);
	const environment = get(relayEnvironmentAtom);

	if (!environment || !cloudId) {
		return { data: [], error: null };
	}

	try {
		// Fire the new GraphQL `searchCombinedConfig` and the legacy
		// `requestKnowledgeSources` REST call in parallel. The legacy call
		// is fired purely for comparison/observability while we validate
		// the new endpoint — its result is logged and otherwise discarded.
		// TODO: remove the parallel `requestKnowledgeSources` call (and
		// the corresponding console.log) once we're confident in the
		// GraphQL response.
		const rovoRequest = requestKnowledgeSources(cloudId);
		const [graphQLResponse, rovoResponse] = await Promise.all([
			withTimingAdapter(
				'operational.appConnectorsRequest.completed.twgMicrositeAppConnectors',
				searchCombinedConfig(environment, cloudId),
				(result) => ({
					requestName: 'searchCombinedConfig',
					success: result.ok && result.value.success === true,
				}),
			),
			withRequestTiming(
				'operational.appConnectorsRequest.completed.twgMicrositeAppConnectors',
				rovoRequest.execute,
				{ requestName: 'knowledgeSources' },
			),
		]);

		// Use the rovo response to map names and icons to the graphQL response
		const rovoConnectors = rovoResponse.success ? rovoResponse.data.knowledgeSources : [];
		const graphQLConnectors = graphQLResponse.success
			? (graphQLResponse.configuration.thirdPartyProducts ?? [])
			: [];

		// Case: Both requests failed
		if (!rovoResponse.success && !graphQLResponse.success) {
			return {
				data: [],
				error: `Failed to fetch connectors: ${
					graphQLResponse.error instanceof Error
						? graphQLResponse.error.message
						: String(graphQLResponse.error ?? '')
				}`,
			};
		}

		// Case: No connectors found
		if (rovoConnectors.length === 0 && graphQLConnectors.length === 0) {
			return {
				data: [],
				error: null,
			};
		}
		// case: graphql gave us nothing but rovo found something
		if (graphQLConnectors.length === 0) {
			return {
				data: rovoConnectors.map((x) => ({
					displayName: x.friendlyName,
					iconUrl: x.iconUrl,
					outboundAuthUrl: x.outboundAuthUrl,
					isUserOAuthed: x.isUserOAuthed,
				})),
				error: null,
			};
		}

		const finalParsedConnectors: AppConnector[] = graphQLConnectors.flatMap((x): AppConnector[] => {
			const keyName = x.integrationARI.split('ari:cloud:platform::integration/')?.[1] ?? undefined;
			if (keyName === undefined) {
				return [];
			}
			const rovo = rovoConnectors.find((y) => {
				if (
					y.source?.includes(keyName) ||
					y.resourceType?.includes(keyName) ||
					(y.resourceType ? y.resourceType.split('_').join('-') : undefined) === keyName
				) {
					return y;
				}
			});
			if (rovo !== undefined) {
				return [
					{
						displayName: rovo.friendlyName,
						iconUrl: rovo.iconUrl,
						outboundAuthUrl: rovo.outboundAuthUrl,
						isUserOAuthed: rovo.isUserOAuthed,
					},
				];
			}
			return [
				{
					displayName: keyName.split('-').map(upperFirst).join(' '),
					iconUrl: `/gateway/api/assist/rovo/v1/resources/icon/${keyName}`,
					outboundAuthUrl: x.outboundAuthUrl,
					isUserOAuthed: false,
				},
			];
		});
		return {
			data: finalParsedConnectors,
			error: null,
		};
	} catch (error) {
		return {
			data: [],
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
});

/**
 * Result type returned by CollaborationUsersAtom.
 * On success, `data` contains the recommended collaboration users and
 * `error` is null. On failure, `data` is an empty array and `error`
 * holds the message so consumers can fall back gracefully.
 */
export type CollaborationUsersResult = {
	data: CollabGraphRecommendedEntity[];
	error: string | null;
};

/**
 * Derived async atom that fetches the user's top collaboration users
 * based on the current cloud ID and account ID.
 *
 * Because it reads from `cloudIdAtom` and `accountIdAtom`, Jotai will
 * evaluate this atom at the same time as `KnowledgeSourcesAtom` (both
 * are kicked off when their consuming components first mount under the
 * shared Suspense boundary in `Home`). It will also automatically
 * re-fetch whenever either dependency changes.
 *
 * If the cloud ID or account ID is not yet available, returns an empty
 * result without hitting the network.
 *
 * Never throws — on failure, returns `{ data: [], error: "..." }` so
 * consumers can handle the error gracefully without an ErrorBoundary.
 *
 * Components using this atom will suspend (React Suspense) until the
 * data is resolved. Wrap the consuming component in a
 * `<Suspense fallback={...}>` boundary.
 */
export const CollaborationUsersAtom: Atom<Promise<CollaborationUsersResult>> = atom<
	Promise<CollaborationUsersResult>
>(async (get) => {
	const cloudId = get(cloudIdAtom);
	const accountId = get(accountIdAtom);

	if (!cloudId || !accountId) {
		return { data: [], error: null };
	}

	try {
		const { execute } = requestCollaborationUsers({
			requestingUserId: accountId,
			targetUserId: accountId,
			tenantId: cloudId,
			limit: 5,
		});
		const response = await withRequestTiming(
			'operational.collaborationUsersRequest.completed.twgMicrositeCollaborationUsers',
			execute,
		);
		if (response.success) {
			return { data: response.data.recommendedEntities, error: null };
		}
		return {
			data: [],
			error: `Failed to fetch collaboration users: ${response.error?.message ?? response.error}`,
		};
	} catch (error) {
		return {
			data: [],
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
});

/**
 * Result type returned by GlobalMetricsAtom.
 * On success, `data` contains the platform-wide metrics row and `error` is null.
 * On failure, `data` is `null` and `error` holds the message so consumers can
 * fall back to the existing hard-coded placeholder strings.
 */
export type GlobalMetricsResult = {
	data: GlobalMetricsResponse | null;
	error: string | null;
};

/**
 * Derived async atom that fetches the unauthenticated platform-wide hero
 * metrics from `/twg-api/v1/microsite/metrics/global`.
 *
 * The endpoint is anonymous and edge-cached at the CDN for 15 minutes, so this
 * atom has no React/Jotai dependencies and only fires once per page load. It
 * is consumed by `Stats` (rendered in the logged-out home view) under the
 * Suspense boundary in `LoggedOutView`.
 *
 * Never throws — on failure, returns `{ data: null, error: "..." }` so
 * consumers can fall back gracefully without an ErrorBoundary.
 */
export const GlobalMetricsAtom: Atom<Promise<GlobalMetricsResult>> = atom<
	Promise<GlobalMetricsResult>
>(async () => {
	try {
		const { execute } = requestGlobalMetrics();
		const response = await withRequestTiming(
			'operational.globalMetricsRequest.completed.twgMicrositeGlobalMetrics',
			execute,
		);
		if (response.success) {
			return { data: response.data, error: null };
		}
		return {
			data: null,
			error: `Failed to fetch global metrics: ${response.error?.message ?? response.error}`,
		};
	} catch (error) {
		return {
			data: null,
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
});

/**
 * Result type returned by OrgMetricsAtom.
 *
 * On success, `data` contains either the full org-scoped metrics payload or a
 * `NoDataResponse` sentinel when the upstream dbt model has not yet produced
 * rows for the org; `error` is null in both cases. On failure, `data` is
 * `null` and `error` holds the message so consumers can fall back to the
 * existing hard-coded placeholder strings.
 *
 * Consumers should narrow on `data.status === 'NO_DATA'` to render an
 * empty-state separately from the populated metrics view.
 */
export type OrgMetricsResult = {
	data: OrgMetricsResponse | NoDataResponse | null;
	error: string | null;
};

/**
 * Derived async atom that fetches the authenticated org-scoped hero metrics
 * from `/twg-api/v1/microsite/metrics/org/{orgId}`.
 *
 * Because it reads from `orgIdAtom` and `cloudIdAtom`, Jotai will
 * automatically re-fetch whenever either dependency changes (e.g. when the
 * user switches tenants). It is consumed by the logged-in home view under a
 * shared Suspense boundary alongside `KnowledgeSourcesAtom` and
 * `CollaborationUsersAtom`.
 *
 * If the org ID or cloud ID is not yet available, returns an empty result
 * without hitting the network.
 *
 * Never throws — on failure, returns `{ data: null, error: "..." }` so
 * consumers can fall back gracefully without an ErrorBoundary.
 */
export const OrgMetricsAtom: Atom<Promise<OrgMetricsResult>> = atom<Promise<OrgMetricsResult>>(
	async (get) => {
		const source = get(orgMetricsSourceAtom);

		// Debug panel override: when the source is `'static'` and a dataset has
		// been picked, serve it directly without touching the network. The
		// payload conforms to `OrgMetricsResponse`, so downstream consumers
		// (e.g. `selectMetrics`) treat it identically to a live response.
		if (source === 'static') {
			const datasetKey = get(selectedStaticDatasetKeyAtom);
			if (datasetKey) {
				return { data: STATIC_DATASETS[datasetKey].metrics, error: null };
			}
			return { data: null, error: null };
		}

		const orgId = get(orgIdAtom);
		const cloudId = get(cloudIdAtom);
		const accountId = get(accountIdAtom);

		if (!orgId || !cloudId) {
			return { data: null, error: null };
		}

		try {
			const { execute } = requestOrgMetrics(orgId, cloudId, accountId);
			const response = await withRequestTiming(
				'operational.orgMetricsRequest.completed.twgMicrositeOrgMetrics',
				execute,
			);
			if (response.success) {
				return { data: response.data, error: null };
			}
			return {
				data: null,
				error: `Failed to fetch org metrics: ${response.error?.message ?? response.error}`,
			};
		} catch (error) {
			return {
				data: null,
				error: error instanceof Error ? error.message : 'An unexpected error occurred',
			};
		}
	},
);
